import { useMemo } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Info,
  Send,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { EnterpriseDetailRecord } from '@/components/enterprise/EnterpriseRecordDetailSheet';
import type { PartnerSenderHealthRow } from '@/hooks/useEnterpriseEmailMonitoring';

interface SenderHealthPanelProps {
  senders: PartnerSenderHealthRow[];
  partnerId: string;
  /** Open a sender's detail in the inline Sheet (no workspace switch). */
  onSelect: (record: EnterpriseDetailRecord) => void;
}

type HealthStatus = PartnerSenderHealthRow['health_status'];

/** Sort weight — needs_reconnect/expired float to the top. */
const STATUS_ORDER: Record<HealthStatus, number> = {
  needs_reconnect: 0,
  expired: 1,
  connected: 2,
};

function StatusBadge({ status }: { status: HealthStatus }): JSX.Element {
  switch (status) {
    case 'needs_reconnect':
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertOctagon className="h-3 w-3" />
          Needs reconnect
        </Badge>
      );
    case 'expired':
      return (
        <Badge variant="default" className="gap-1 bg-yellow-500 hover:bg-yellow-500">
          <AlertTriangle className="h-3 w-3" />
          Expired
        </Badge>
      );
    case 'connected':
    default:
      return (
        <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700">
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
  }
}

/**
 * M2 (#572): per-sender health board across an enterprise partner's workspaces.
 *
 * Rows needing action (needs_reconnect, then expired) float to the top with
 * red/yellow badges; connected senders are green. Outlook SPA accounts get an
 * info chip (the 24h refresh-cap risk). Reconnect is NOT auto-fixed here — the
 * remediation is a per-workspace user action, so we surface a hint pointing at
 * that workspace's Settings → Senders.
 */
export function SenderHealthPanel({
  senders,
  partnerId,
  onSelect,
}: SenderHealthPanelProps): JSX.Element {
  const sorted = useMemo(
    () =>
      [...senders].sort((a, b) => {
        const byStatus = STATUS_ORDER[a.health_status] - STATUS_ORDER[b.health_status];
        if (byStatus !== 0) return byStatus;
        if (a.workspace_name !== b.workspace_name) {
          return a.workspace_name.localeCompare(b.workspace_name);
        }
        return a.email_address.localeCompare(b.email_address);
      }),
    [senders],
  );

  return (
    <Card data-partner-id={partnerId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          Sender health
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <div>No sender accounts</div>
            <div className="text-sm">
              No email accounts are connected across these workspaces yet.
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workspace</TableHead>
                <TableHead>Sender</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((s) => {
                const needsAction =
                  s.health_status === 'needs_reconnect' || s.health_status === 'expired';
                return (
                  <TableRow key={`${s.workspace_id}:${s.email_address}`}>
                    <TableCell className="text-sm text-muted-foreground">
                      {s.workspace_name}
                    </TableCell>
                    <TableCell className="text-sm font-medium">{s.email_address}</TableCell>
                    <TableCell className="text-sm capitalize">{s.provider}</TableCell>
                    <TableCell>
                      <StatusBadge status={s.health_status} />
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        {s.outlook_spa_24h_risk && (
                          <Badge variant="outline" className="gap-1 text-blue-600">
                            <Info className="h-3 w-3" />
                            Outlook 24h refresh risk
                          </Badge>
                        )}
                        {needsAction && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            Reconnect in that workspace&rsquo;s Settings → Senders
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {needsAction && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onSelect({ type: 'sender', data: s })}
                        >
                          Details
                          <ChevronRight className="ml-1.5 h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
